import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, CircleAlert } from "lucide-react";
import { useAdminApi, errMsg } from "@/lib/site";
import { AdminTitle } from "./AdminMessages";

const Telegram = ({ s, refresh }) => {
  const call = useAdminApi();
  const [code, setCode] = useState(s.telegram_link_code);
  const gen = async () => { try { setCode((await call("post", "/admin/telegram/link-code")).code); } catch (e) { toast.error(errMsg(e)); } };
  const unlink = async () => { await call("post", "/admin/telegram/unlink"); refresh(); };
  return (
    <div className="rounded-3xl bg-white border p-6 space-y-3" data-testid="admin-telegram-card">
      <div className="flex items-center gap-2"><Send className="w-5 h-5 text-sky-500" /><p className="font-display font-bold">Telegram alerts & replies</p></div>
      {!s.telegram_configured ? (
        <p className="text-sm text-slate-600 flex gap-2" data-testid="admin-telegram-not-configured"><CircleAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />The Telegram bot isn't connected yet. Chats still arrive in this inbox.</p>
      ) : s.telegram_linked ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-emerald-700 flex gap-2" data-testid="admin-telegram-linked"><CheckCircle2 className="w-4 h-4" />Connected{s.telegram_bot ? ` to @${s.telegram_bot}` : ""}. Reply to a message in Telegram to answer the visitor.</p>
          <button onClick={unlink} className="text-sm text-slate-500 hover:text-red-600" data-testid="admin-telegram-unlink-btn">Disconnect</button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">1. Get a code below. 2. Open {s.telegram_bot ? <a className="text-blue-600 underline" href={`https://t.me/${s.telegram_bot}`} target="_blank" rel="noreferrer">@{s.telegram_bot}</a> : "your bot"} in Telegram and send it <span className="font-mono">/link CODE</span>. 3. Refresh this page.</p>
          {code && <p className="font-mono text-2xl font-bold tracking-widest" data-testid="admin-telegram-code">/link {code}</p>}
          <div className="flex gap-2">
            <button onClick={gen} className="rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-semibold" data-testid="admin-telegram-code-btn">{code ? "New code" : "Get code"}</button>
            <button onClick={refresh} className="rounded-full border px-4 py-2 text-sm" data-testid="admin-telegram-refresh-btn">Refresh</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Users = () => {
  const call = useAdminApi();
  const [d, setD] = useState(null);
  const load = useCallback(() => call("get", "/users").then(setD).catch(() => {}), [call]);
  useEffect(() => { load(); }, [load]);
  const setRole = async (u, role) => { try { await call("patch", `/users/${u.user_id}/role`, { role }); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid="admin-users-card">
      <p className="font-display font-bold mb-3">People who have signed in</p>
      <div className="divide-y">
        {d.users.map((u) => (
          <div key={u.user_id} className="flex items-center justify-between gap-3 py-2.5">
            <span className="text-sm truncate">{u.email || u.user_id}</span>
            <select value={u.role} onChange={(e) => setRole(u, e.target.value)} className="rounded-lg border px-2 py-1 text-sm" data-testid={`admin-user-role-${u.user_id}`}>
              {d.roles.map((r) => <option key={r} value={r}>{r === "admin" ? "Admin" : "No access"}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminSettings() {
  const call = useAdminApi();
  const [s, setS] = useState(null);
  const load = useCallback(() => call("get", "/admin/settings").then(setS).catch((e) => toast.error(errMsg(e))), [call]);
  useEffect(() => { load(); }, [load]);
  if (!s) return <p className="text-slate-400">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="admin-settings">
      <AdminTitle sub="Connections and access">Settings</AdminTitle>
      <Telegram s={s} refresh={load} />
      <div className="rounded-3xl bg-white border p-6 space-y-2" data-testid="admin-payments-status-card">
        <p className="font-display font-bold">Payments</p>
        <p className="text-sm text-slate-600">Card (Stripe): <span className="font-semibold">hidden from checkout (test mode only)</span></p>
        <p className="text-sm text-slate-600" data-testid="admin-paypal-status">PayPal: <span className="font-semibold">{s.paypal_enabled ? (s.paypal_mode === "live" ? "live" : "test mode (sandbox)") : "not connected"}</span></p>
      </div>
      <Users />
    </div>
  );
}
